import React, { useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * Reusable "Install app" button.
 * - Hidden when the app is already running as a PWA (standalone mode).
 * - Hidden if the browser doesn't support installability AND it isn't iOS Safari.
 * - On Chromium/Android: triggers the native install prompt directly.
 * - On iOS Safari: opens a small instruction toast (iOS doesn't expose programmatic install).
 *
 * `variant`:
 *   - "primary"  → solid forest-green pill (use in hero / CTA areas)
 *   - "ghost"    → outline, smaller (use in header)
 *   - "compact"  → very small text-only (use inline)
 */
export default function InstallAppButton({ variant = "primary", className = "", children }) {
  const [deferred, setDeferred] = useState(null);
  const [supported, setSupported] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    // Hide if installed
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) { setHidden(true); return; }

    const ua = navigator.userAgent || "";
    const onIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const onSafari = /^((?!chrome|android).)*safari/i.test(ua);
    setIsIOS(onIOS && onSafari);

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setSupported(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  if (hidden) return null;
  if (!supported && !isIOS) return null; // browser can't install — don't tease

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice?.outcome === "accepted") setHidden(true);
      return;
    }
    if (isIOS) {
      toast(
        "Tap the Share icon below, then 'Add to Home Screen' to install GreenBelt.",
        { duration: 7000, description: "Opens like a native app, full-screen." }
      );
    }
  };

  const base = "inline-flex items-center gap-2 transition-colors whitespace-nowrap";
  const styleMap = {
    primary: `${base} bg-ink text-bg px-6 py-3 hover:bg-leaf font-mono text-xs uppercase tracking-[0.18em]`,
    ghost: `${base} border border-ink text-ink px-4 py-2 hover:bg-ink hover:text-bg font-mono text-[11px] uppercase tracking-[0.18em]`,
    compact: `${base} text-leaf hover:text-ink underline underline-offset-4 font-mono text-xs`,
  };
  const cls = styleMap[variant] || styleMap.primary;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="install-app-button"
      className={`${cls} ${className}`}
      aria-label="Install GreenBelt app"
    >
      <span aria-hidden="true">📲</span>
      {children || "Install app"}
    </button>
  );
}
